/**
 * Produced-files list with Excel download and lineage (P2-05 / P2-15).
 *
 * The row count renders only when it is meaningful (> 0): a mapping
 * workbook whose For txt was withheld is still a complete deliverable —
 * "EXCEL – 0 ROWS" was a defect, not information. A failed lineage fetch
 * renders as an error with retry, never as "Original upload".
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useNavigate } from "react-router-dom";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { api, type Lineage } from "../lib/api";
import { ID_SLICE_LEN } from "../lib/config";
import { errorText } from "../lib/errors";
import { useToast } from "../hooks/useToast";
import { ErrorAlert } from "./ui/ErrorAlert";

type Artifact = {
  artifact_id: string;
  kind: string;
  display_name: string;
  row_count: number;
};

type LineageState =
  | { status: "loading" }
  | { status: "error"; error: unknown }
  | { status: "ok"; lineage: Lineage };

/** Operator wording for a lineage relation; unknown ones show as declared. */
function relationKey(relation: string): string {
  return (
    {
      derived: "files.relationDerived",
      replaced_by_upload: "files.relationReplaced",
      revised_by_agent: "files.relationRevised",
    }[relation] ?? ""
  );
}

export function ArtifactFiles({ artifacts }: { artifacts: Artifact[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [lineage, setLineage] = useState<LineageState | null>(null);

  // authenticated fetch-and-save — a plain href carries no bearer token
  async function download(artifact: Artifact) {
    try {
      await api.downloadExcel(artifact.artifact_id, artifact.display_name);
    } catch (err) {
      toast(errorText(err), "error");
    }
  }

  async function loadLineage(artifactId: string) {
    setOpenFor(artifactId);
    setLineage({ status: "loading" });
    try {
      const result = await api.lineage(artifactId);
      setLineage({ status: "ok", lineage: result });
    } catch (err) {
      setLineage({ status: "error", error: err });
    }
  }

  function toggleLineage(artifactId: string) {
    if (openFor === artifactId) {
      setOpenFor(null);
      setLineage(null);
      return;
    }
    void loadLineage(artifactId);
  }

  return (
    <List dense disablePadding>
      {artifacts.map((artifact) => (
        <ListItem
          key={artifact.artifact_id}
          disableGutters
          sx={{ display: "block", borderBottom: 1, borderColor: "divider", py: 1 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListItemText
              primary={
                <Link
                  component="button"
                  type="button"
                  onClick={() => void download(artifact)}
                  underline="hover"
                  className="num"
                  sx={{ fontWeight: 600, fontSize: 14 }}
                >
                  {artifact.display_name}
                </Link>
              }
              secondary={
                artifact.row_count > 0
                  ? `${artifact.kind.toUpperCase()} · ${t("files.rows", {
                      count: artifact.row_count,
                    })}`
                  : `${artifact.kind.toUpperCase()} · ${t("files.noRows")}`
              }
            />
            <Tooltip title={t("files.download")}>
              <IconButton
                size="small"
                onClick={() => void download(artifact)}
                aria-label={`${t("files.download")} — ${artifact.display_name}`}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("files.fork")}>
              <IconButton
                size="small"
                onClick={() =>
                  navigate(
                    `/tool?artifact=${encodeURIComponent(artifact.artifact_id)}&name=${encodeURIComponent(artifact.display_name)}`,
                  )
                }
                aria-label={`${t("files.fork")} — ${artifact.display_name}`}
              >
                <CallSplitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("files.lineage")}>
              <IconButton
                size="small"
                onClick={() => toggleLineage(artifact.artifact_id)}
                aria-label={`${t("files.lineage")} — ${artifact.display_name}`}
                aria-expanded={openFor === artifact.artifact_id}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {openFor === artifact.artifact_id && lineage && (
            <Box sx={{ pl: 2, pt: 0.5 }}>
              {lineage.status === "loading" && <CircularProgress size={16} />}
              {lineage.status === "error" && (
                <ErrorAlert
                  error={lineage.error}
                  onRetry={() => void loadLineage(artifact.artifact_id)}
                />
              )}
              {lineage.status === "ok" && lineage.lineage.chain.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t("files.lineageOriginal")}
                </Typography>
              )}
              {lineage.status === "ok" &&
                lineage.lineage.chain.map((edge) => (
                  <Typography
                    key={`${edge.artifact_id}-${edge.parent_artifact_id}`}
                    variant="caption"
                    display="block"
                  >
                    {edge.artifact_name ?? edge.artifact_id.slice(0, ID_SLICE_LEN)} ←{" "}
                    {edge.parent_name ?? edge.parent_artifact_id.slice(0, ID_SLICE_LEN)}{" "}
                    <Chip
                      size="small"
                      label={relationKey(edge.relation) ? t(relationKey(edge.relation)) : edge.relation}
                      color={edge.relation === "replaced_by_upload" ? "warning" : "success"}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                    {edge.parent_job_id && (
                      <>
                        {" · "}
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => navigate(`/runs/${edge.parent_job_id}`)}
                        >
                          {t("files.fromRun", {
                            id: edge.parent_job_id.slice(0, ID_SLICE_LEN),
                          })}
                        </Link>
                      </>
                    )}
                  </Typography>
                ))}
              {lineage.status === "ok" && (lineage.lineage.used_by?.length ?? 0) > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t("files.usedBy")}
                  </Typography>
                  {lineage.lineage.used_by?.map((child) => (
                    <Typography key={child.artifact_id} variant="caption" display="block">
                      {child.display_name}
                      {child.job_id && (
                        <>
                          {" · "}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => navigate(`/runs/${child.job_id}`)}
                          >
                            {t("files.inRun", { id: child.job_id.slice(0, ID_SLICE_LEN) })}
                          </Link>
                        </>
                      )}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </ListItem>
      ))}
    </List>
  );
}
